import { createClient } from '@supabase/supabase-js';
import { logError } from '@/lib/sentry';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// 개발 환경인지 확인 (로그 출력 용도로만 사용)
const isDev = import.meta.env.DEV;

// 로그 비활성화 설정 - GoTrueClient 로그 억제
const suppressLogs = true;

// Supabase 클라이언트 설정
const supabaseOptions = {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true, // URL의 토큰 자동 감지
    flowType: 'implicit' as const, // OAuth flow 타입 설정
    // 디버그 모드 비활성화 (GoTrueClient 로그 억제)
    debug: false,
    // storage - 안정성을 위해 확장된 로컬 스토리지 사용
    storage: {
      getItem: (key: string) => {
        try {
          const value = localStorage.getItem(key);
          if (isDev && !suppressLogs) console.log(`🔐 Auth 스토리지 읽기: ${key}`);
          return value;
        } catch (error) {
          logError('스토리지 접근 오류:', error);
          return null;
        }
      },
      setItem: (key: string, value: string) => {
        try {
          localStorage.setItem(key, value);
          if (isDev && !suppressLogs) console.log(`🔐 Auth 스토리지 쓰기: ${key}`);
          return;
        } catch (error) {
          logError('스토리지 저장 오류:', error);
        }
      },
      removeItem: (key: string) => {
        try {
          localStorage.removeItem(key);
          if (isDev && !suppressLogs) console.log(`🔐 Auth 스토리지 삭제: ${key}`);
          return;
        } catch (error) {
          logError('스토리지 항목 제거 오류:', error);
        }
      }
    }
  },
  global: {
    // 매 API 호출마다 Supabase 헤더 추가
    headers: {
      'x-application-name': 'seller-helper'
    }
  },
  // 안정성을 위한 추가 옵션
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
};

// Supabase 클라이언트 생성
export const supabase = createClient(supabaseUrl, supabaseAnonKey, supabaseOptions);

// 개발 환경에서도 GoTrueClient 로그는 비활성화
if (isDev && !suppressLogs) {
  // 인증 이벤트 리스너 추가 (최소한의 로그만 출력)
  supabase.auth.onAuthStateChange((event) => {
    console.log(`🔒 인증 상태 변경: ${event}`);
  });
}

// 로그인된 유저 확인 함수
export const checkUser = async () => {
  const {
    data: { user }
  } = await supabase.auth.getUser()
  
  if (user) {
    // 개발 환경에서만 로그 출력 (최소한으로 유지)
    if (isDev && !suppressLogs) {
      console.log("✅ 로그인됨")
    }
    return user
  } else {
    // 개발 환경에서만 로그 출력 (최소한으로 유지)
    if (isDev && !suppressLogs) {
      console.log("❌ 로그인 안됨")
    }
    return null
  }
}